import useInput from 'lib/hooks/useInput';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  AppActions,
  NotificationActions,
  PartnerActions,
  ProjectActions,
} from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { isLogInSelector } from 'store/modules/auth';
import io from 'socket.io-client';
import store from 'store';
import { BASE_SOCKET_PRIVATE_URL } from 'lib/setting';
import { useDidUpdateEffect } from 'lib/utils';
import { Trans, useTranslation } from 'react-i18next';
import { cutUrl } from 'lib/library';
import { useHistory, useLocation } from 'react-router-dom';
import { AppContext } from './AppContext';
import { pageUrl } from 'lib/mapper';
import AlertModalContent, {
  ModalCustomButton,
} from 'components/common/modal/contents/AlertModalContent';
import { T } from 'components/common/text';
import CustomSpan from 'components/common/text/CustomSpan';
import { useCloudManageStore } from 'store/local/cloudManage';
// import { AppActions, EventActions } from 'store/actionCreators';

export const PrivateSocketContext = createContext<any>(null);

interface PrivateSocketProviderProps {
  value?: any;
  children: React.ReactNode;
}

export function PrivateSocketProvider({ value, children }: PrivateSocketProviderProps) {
  const { language, isLogIn, user } = useShallowAppSelector(state => ({
    language: state.base.language,
    isLogIn: isLogInSelector(state),
    user: state.user.user,
  }));
  const { t } = useTranslation();
  const socketState = useRef<any>(null);
  const [realTimeChat, setRealTimeChat] = useState(null);
  const [realTimeEvent, setRealTimeEvent] = useState(null);
  const [newPartnerListStack, setNewPartnerListStack] = useState<any[]>([]);
  const [updatedProjectParams, setUpdatedProjectParams] = useState<
    | {
        projectCode: string;
        projectName: string;
        stage?: number;
      }
    | any
  >({});
  const [updatedPartnerParams, setUpdatedPartnerParams] = useState<any>({});
  // change new Events 변경 알림 -> NotificationContainer 에서 받아서 reFetch
  const hasNewEvents = useInput(null); // boolean | null
  const hasNewMillingEvents = useInput(null); // boolean | null
  const newEventType = useInput('');
  const projectAcceptEvent = useInput('');
  const { pathname } = useLocation();
  const history = useHistory();
  const isPartnerPage = cutUrl(pathname) === 'partner';
  const isProjectDetailPage = `${cutUrl(pathname)}/${cutUrl(pathname, 1)}` === 'project/detail';
  const { userTypeIdx } = useContext(AppContext);

  const { setCloudManageOpen } = useCloudManageStore();
  // useEffect(() => {
  //   console.log('socketState', socketState);
  // }, [socketState]);

  useEffect(() => {
    if (isLogIn) {
      const socket = io(BASE_SOCKET_PRIVATE_URL, {
        path: '/notification/socket.io',
        reconnectionDelayMax: 10000,
        autoConnect: false,
        auth: { 'x-access-token': store.getState().auth.accessToken },
      });
      socket.open();
      socketState.current = socket;

      socket.on('connect', () => {
        console.log('private socket connect_', socket.id, socket.connected);
        // emit auth
        handleEmitAuth();
      });

      socket.on('disconnect', () => {
        console.log('private socket connect_dis', socket.connected);
        // console.log('socket.id', socket.id);
      });

      socket.on('connect_error', error => {
        console.log('private socket connect_error', error);
        socket.close();
      });

      // partner event 시
      NotificationActions.fetch_new_notifications_request({ language: language.toUpperCase() });

      // 채팅, 파일업로드, 타임라인, Sync 알림 - 우측하단 실시간 알림(show_toast)

      // e.g
      // category: "PARTNER"
      // fromSyncUid: "332026097773583502210"
      // params: {userGroupCode: '211104789766HPUIBJ'}
      // title: "파트너 요청 도착"
      // type: "NEW_PARTNER_REQ"

      // NOTE: 우측 하단에 전부 띄우는 알림, Notifications, Sync(승낙/거부)
      // { fromName: string; type:string; enrollDate: number; }
      // 새로운 notifications 왔을 경우 fetch new events
      socket.on('notification', data => {
        console.log('socket notification', data);
        const { category, type, fromName, params } = data || {
          category: '',
          type: '',
          fromName: '',
          params: {},
        };

        // request api
        NotificationActions.fetch_new_notifications_request({ language: language.toUpperCase() });
        hasNewEvents.setValue(true);
        if (!type) return;
        newEventType.setValue(type);

        if (type === 'PROJECT_UPDATE') {
          // { ..., params: { projectCode: string; stage?: number; } }
          if (!params?.projectCode) return;
          if (pathname.includes('util/images/')) return;
          // project hasNewEvent갱신
          setUpdatedProjectParams({ ...params, hasNewEvent: true });

          // 페이지 밖의 유저에게만 toast 알림
          return;
        }

        let relatedName = '';
        if (category === 'PROJECT') relatedName = params?.projectName;
        if (category === 'PARTNER') relatedName = fromName;
        if (category === 'STORE') relatedName = params?.productName;

        const toastParams: any = {
          delay: 5000,
          isAutoRemove: true,
          message: t(`NOTIFICATION_EVENT_TYPE_${type}`),
          relatedName,
        };

        // if (type === 'NEW_PARTNER_REQ') {
        // }
        if (type === 'PARTNER_ACCEPT') {
          setUpdatedPartnerParams({ ...params });
        }
        if (type === 'PROJECT_ACCEPT') {
          //파트너 Accept 후, 프로젝트 refetch를 위한 코드 전송
          console.log('HDS] PROJECT_ACCEPT');

          projectAcceptEvent.setValue(params?.projectCode);
        }

        if (category === 'ACCOUNT') {
          AppActions.add_popup({
            isOpen: true,
            isCloseIcon: true,
            width: 570,
            title: '',
            content: (
              <AlertModalContent
                title={t('NOTIFICATION_CLOUD_STORAGE')}
                mainText={t('NOTIFICATION_CLOUD_DATA_REMAIN', {
                  remain: params?.remainRatio,
                })}
                subText={
                  <Trans
                    defaults="CLOUD_REMAIN_DETAIL"
                    components={[
                      <CustomSpan fontColor={'#D85066'}>
                        {{ remain: params?.remainSizeMB }}
                      </CustomSpan>,
                    ]}
                  />
                }
              />
            ),
            button: <ModalCustomButton text={<T>CLOUD_DATA_CLEAR</T>} />,
            onClick() {
              history.push(pageUrl.user.index);
              setCloudManageOpen(true);
              return;
            },
          });
        }

        // PENDING: params.partnerName 있을 경우, 알림에 같이 보여주기
        AppActions.show_toast(toastParams);
      });

      // NOTE: 요청 변경으로 보류

      // // NOTE: Chat
      // // { fromName: string; type: string; enrollDate: number; params: { projectCode: string; projectName: string; } }
      // socket.on('realTimeChat', data => {
      //   console.log('socket realTimeChat', data);
      //   setRealTimeChat(data);
      //   AppActions.show_toast({
      //     delay: 5000,
      //     isAutoRemove: false,
      //     // ...convertEventMessage({ eventType, eventTitle, params }),
      //     message: 'A message has arrived.',
      //   });
      // });

      // // NOTE: FileUpload, Timeline change
      // // { fromName: string; type: string; enrollDate: number; params: { projectCode: string; projectName: string; content?: string; } }
      // socket.on('realTimeEvent', data => {
      //   console.log('socket realTimeEvent', data);
      //   const { type } = data;
      //   setRealTimeEvent(data);

      //   if (type === 'FILE_UPLOAD') {
      //     // FileUpload
      //     AppActions.show_toast({
      //       delay: 5000,
      //       isAutoRemove: false,
      //       message: data?.params?.content,
      //     });
      //   } else {
      //     // Timeline
      //     AppActions.show_toast({
      //       delay: 5000,
      //       isAutoRemove: false,
      //       message: t(`NOTIFICATION_EVENT_TYPE_${type}`),
      //     });
      //   }
      // });
      socket.on('signoutNotification', (data: any) => {
        // 회원탈퇴 시, 해당 유저로 접속중인 경우 로그 아웃처리
        history.push(pageUrl.auth.signOut);
      });

      socket.on('millingEvent', (data: any) => {
        console.log(socket.id, ' millingEvent ', data);
        const { eventTypeIdx, eventType, syncUid: chatSyncUid, result = null } = data;
        console.log('eventType', eventType);
        hasNewMillingEvents.setValue(true);
        // console.log('projectEvent.designerEnter', projectEvent.designerEnter);
        // if (result === 500) setChatResponseStatus(result);

        // const toastParams: any = {
        //   delay: 5000,
        //   isAutoRemove: true,
        //   message: t(`NOTIFICATION_EVENT_TYPE_${type}`),
        //   relatedName,
        // };

        // // if (type === 'NEW_PARTNER_REQ') {
        // // }
        // if (type === 'PARTNER_ACCEPT') {
        //   setUpdatedPartnerParams({ ...params });
        // }

        // // PENDING: params.partnerName 있을 경우, 알림에 같이 보여주기
        // AppActions.show_toast(toastParams);
      });
    }
  }, [isLogIn]);

  // not in project detail page, notify
  useDidUpdateEffect(() => {
    if (updatedProjectParams?.projectCode) {
      if (pathname !== `/project/detail/${updatedProjectParams?.projectCode}`) {
        // console.log('updatedProjectParams', updatedProjectParams);
        // console.log('projectUpdate noti 2');
        ProjectActions.set_projects({ ...updatedProjectParams, hasNewEvent: true });

        AppActions.show_toast({
          id: updatedProjectParams.projectCode,
          delay: 5000,
          isAutoRemove: true,
          message: t(`NOTIFICATION_EVENT_TYPE_${newEventType.value}`),
          relatedName: updatedProjectParams.projectName,
          projectCode: updatedProjectParams.projectCode,
        });
      }
      setUpdatedProjectParams({});
    }
  }, [updatedProjectParams]);

  // in partner page, partner socket data
  useDidUpdateEffect(() => {
    if (isPartnerPage) {
      if (newEventType.value === 'NEW_PARTNER_REQ') {
        return PartnerActions.fetch_partner_receive_requested_list_request();
      }
      if (newEventType.value === 'PARTNER_ACCEPT' && updatedPartnerParams?.partnershipCode) {
        const {
          accountType,
          email,
          userGroupCode: groupCode,
          modifyDate,
          name,
          userTypeIdx,
          partnershipCode,
          partnershipIdx,
          profileImg,
          userGroupIdx,
        } = updatedPartnerParams;

        PartnerActions.remove_partner_request({ partnershipCode });
        setNewPartnerListStack(draft => [
          {
            accountType,
            email,
            groupCode,
            modifyDate,
            name,
            userTypeIdx,
            partnershipCode,
            partnershipIdx,
            profileImg,
            userGroupIdx,
          },
          ...draft,
        ]);
      }
    }
  }, [isPartnerPage, newEventType.value, updatedPartnerParams]);

  // signOut 발생시 socket close
  useDidUpdateEffect(() => {
    if (!!socketState.current && !user) {
      console.log('private socket close');
      socketState.current.close();
      // setIsConnect(null);
    }
  }, [socketState.current, user]);

  const handleEmitAuth = () => {
    const { email, syncUid, groupCode, millingSerialList } = user;
    if (!email || !syncUid || !groupCode)
      return AppActions.show_toast({
        type: 'error',
        message: 'Private Socket Auth\n Bad Request 400',
      });
    console.log('private auth', email);
    // TODO: millingSerialList 추가
    socketState.current.emit('auth', { email, syncUid, groupCode, millingSerialList });
  };

  return (
    <PrivateSocketContext.Provider
      value={{
        hasNewEvents,
        realTimeChat,
        realTimeEvent,
        newEventType,
        newPartnerListStack,
        hasNewMillingEvents,
        projectAcceptEvent,
      }}
    >
      {children}
    </PrivateSocketContext.Provider>
  );
}
